import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  trackSignUp,
  trackLogin,
  trackRegistrationAction,
  trackAuthenticationFailed,
} from '../utils/analytics';

// 2026-07: reframed around CONTINUITY — the one thing a guest session can't do
// and a search engine can't replicate. Previously this sold "storage", which
// isn't a reason to hand over an email.
// Two, not four. Four read as a pitch and pushed the form below the fold; these two carry
// the whole promise (it remembers, and it compounds) without restating the subtitle.
// Three, each doing a different job — stores, tracks, improves. The modal has no subtitle
// now, so these carry the whole answer to "why bother?" without repeating one another.
const BENEFITS = [
  { text: 'remembers your skin, shades and products' },
  { text: 'tracks how your skin changes over time' },
  { text: 'gets sharper with every answer' },
];

// The three examples we can actually SHOW (real screens in the carousel), in the same order
// as the carousel slides. Keys map to menu views. Scan was dropped in favour of Routine so
// the tiles and the example carousel line up one-to-one.
const UNLOCKS = [
  { key: 'product-analysis', image: '/journey-icons/product-analysis.webp', scale: 1.8, title: 'Analyse a product', sub: 'Check anything against your skin' },
  { key: 'progress', image: '/journey-icons/skin-journal.webp', scale: 1.55, title: 'Skin Journal', sub: 'Track how your skin changes' },
  { key: 'routine', image: '/journey-icons/routine.webp', scale: 1.85, title: 'My Routine', sub: 'Steps checked for conflicts and timing' },
];

// One example per selector: tapping a welcome tile opens that feature's REAL screen with a
// tailored action CTA, instead of dropping a brand-new user straight into an empty section.
// Images are real captures shipped in public/onboarding/ (journal face is founder-consented).
// ?v=2 cache-bust: the images were re-encoded smaller (commit 0ce4b0f) but the
// filenames are unfingerprinted, so devices/CDNs that cached the old heavy
// max-age=0 copies kept showing the "not loading" behaviour. The query string
// forces a fresh URL so no stale copy is served.
const EXAMPLES = {
  'product-analysis': { img: '/onboarding/analyse.jpg?v=2', cap: 'Check any product against your skin, ingredient by ingredient.', cta: 'Analyse a product' },
  'progress':         { img: '/onboarding/journal.jpg?v=2', cap: 'Track your skin over time.', cta: 'Start my Skin Journal' },
  'routine':          { img: '/onboarding/routine.jpg?v=2', cap: 'Know which products clash, and when to use each.', cta: 'Build my routine' },
};

const PW_RULES = [
  { id: 'len',     label: 'At least 8 characters',      test: (v) => v.length >= 8 },
  { id: 'upper',   label: 'One uppercase letter',        test: (v) => /[A-Z]/.test(v) },
  { id: 'num',     label: 'One number',                  test: (v) => /[0-9]/.test(v) },
  { id: 'special', label: 'One special character (!@#…)', test: (v) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v) },
];

const STRENGTH_COLORS = ['#E05C5C', '#E08C3A', '#C9A84C', '#4A9B6F'];

function FeatureExampleRows({ onSelect }) {
  return (
    <div style={{ textAlign: 'left' }} aria-label="Features included with an account">
      {UNLOCKS.map((item, index) => (
        <button
          key={item.key}
          type="button"
          onClick={() => onSelect(item.key)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            background: '#FAF7F3', border: '1px solid #F0EBE5', borderRadius: 12,
            padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
            marginBottom: index === UNLOCKS.length - 1 ? 0 : 8,
          }}
        >
          <span aria-hidden="true" style={{
            width: 44, height: 44, flexShrink: 0, overflow: 'hidden', borderRadius: 12,
            background: '#F3E5D9', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <img
              src={item.image}
              alt=""
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: `scale(${item.scale})` }}
            />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#341C1C' }}>{item.title}</span>
            <span style={{ display: 'block', fontSize: 13, color: '#8A8079', marginTop: 1 }}>{item.sub}</span>
          </span>
          <span aria-hidden="true" style={{ color: '#C4BCB4', fontSize: 17, lineHeight: 1 }}>›</span>
        </button>
      ))}
    </div>
  );
}

export default function UpgradeAccountModal({ isOpen, onClose, onSuccess, startOnSignIn = false, mandatory = false, savedItems = [], onExplore, previewWelcome = false, skipPitch = false, simpleForm = false }) {
  const { convertToEmailUser, signIn, resetPassword } = useAuth();
  const [mode, setMode] = useState(startOnSignIn ? 'signin' : 'create');
  const touchStartY = React.useRef(null);
  const sheetRef = React.useRef(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  // previewWelcome lets ?preview=welcome open straight to the post-registration view,
  // so the screen can be reviewed without creating a real account in the shared user DB.
  const [justRegistered, setJustRegistered] = useState(previewWelcome);
  // Which feature example is open in the welcome (null = show the tiles).
  const [exampleKey, setExampleKey] = useState(null);
  // Show the value BEFORE the ask. The gate used to open on a form, which asks
  // someone to commit before they've seen what they'd be committing to. Now the
  // same tiles and real screenshots that used to appear AFTER registering come
  // first, and the form is one tap away. Sign-in skips straight to the form:
  // an existing member doesn't need the pitch.
  const [showPitch, setShowPitch] = useState(!startOnSignIn && !skipPitch);
  // 'Almost there' continues a thought the pitch started. Opened cold — from a
  // menu, a sign-in link, anywhere that isn't the two-step gate — it refers to a
  // journey the user never began, so those keep the original standalone wording.
  const [cameFromPitch, setCameFromPitch] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (simpleForm) setJustRegistered(false);
      setMode(startOnSignIn ? 'signin' : 'create');
      setShowPitch(!startOnSignIn && !skipPitch);
      setCameFromPitch(false);
      setError('');
      setMessage('');
      setShowReset(false);
      setResetEmail('');
      setResetSent(false);
      setResetLoading(false);
    }
  }, [isOpen, startOnSignIn, skipPitch, simpleForm]);

  if (!isOpen) return null;

  const pwMet = PW_RULES.map((r) => r.test(password));
  const pwScore = pwMet.filter(Boolean).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (mode === 'create') {
      if (pwScore < 4) {
        setError('Please meet all password requirements.');
        return;
      }
      setIsLoading(true);
      trackRegistrationAction('create_account_submitted', 'account_modal');
      try {
        const { error } = await convertToEmailUser(email, password, firstName, marketingOptIn);
        if (error) {
          trackAuthenticationFailed('create', error.code || error.status || 'rejected');
          setError(error.message);
        } else {
          trackSignUp('email_conversion');
          onSuccess?.();
          if (simpleForm) { onClose(); resetModal(); return; }
          // Don't auto-close on a toast. Registering used to change nothing the user could
          // see, so the promise ("we'll remember this") landed invisibly — the welcome view
          // names what was actually kept, and they close it themselves.
          setJustRegistered(true);
        }
      } catch (error) {
        trackAuthenticationFailed('create', error?.code || 'unexpected');
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(true);
      trackRegistrationAction('sign_in_submitted', 'account_modal');
      try {
        const { error } = await signIn(email, password);
        if (error) {
          trackAuthenticationFailed('signin', error.code || error.status || 'rejected');
          setError(error.message);
        } else {
          trackLogin('email');
          onSuccess?.();
          onClose();
          resetModal();
        }
      } catch (error) {
        trackAuthenticationFailed('signin', error?.code || 'unexpected');
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetModal = () => {
    // In preview mode keep the welcome pinned so tapping a tile / Keep chatting doesn't
    // collapse back to the sign-up form — this is a static review of the welcome screen.
    setJustRegistered(previewWelcome);
    setExampleKey(null);
    setShowPitch(!startOnSignIn && !skipPitch);
    setCameFromPitch(false);
    setMode('create');
    setFirstName('');
    setEmail('');
    setPassword('');
    setShowPw(false);
    setMarketingOptIn(true);
    setError('');
    setMessage('');
    setShowReset(false);
    setResetEmail('');
    setResetSent(false);
    setResetLoading(false);
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    const { error } = await resetPassword(resetEmail);
    setResetLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setResetSent(true);
      setError('');
    }
  };

  const handleClose = () => { onClose(); resetModal(); };

  const switchMode = (m) => {
    setMode(m);
    setTermsAgreed(false);
    setError('');
    setMessage('');
    setPassword('');
    setShowReset(false);
    setResetEmail('');
    setResetSent(false);
  };

  return (
    /* Overlay */
    <div
      onClick={(e) => { if (!mandatory && e.target === e.currentTarget) handleClose(); }}
      style={{
        position: 'fixed', inset: 0,
        // Lighter than a standard modal scrim on purpose: the conversation
        // above should stay legible, so the gate reads as an invitation rather
        // than a wall dropped in front of what they were reading.
        background: 'rgba(0,0,0,0.28)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      {/* Sheet */}
      <div
        ref={sheetRef}
        onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
        onTouchEnd={(e) => {
          if (touchStartY.current === null) return;
          const diff = e.changedTouches[0].clientY - touchStartY.current;
          // Only treat a downward drag as dismiss when the sheet is already
          // scrolled to the top. Otherwise scrolling a tall example image (the
          // finger moves down) was read as a swipe and the whole modal vanished.
          const atTop = !sheetRef.current || sheetRef.current.scrollTop <= 0;
          if (diff > 80 && atTop) handleClose();
          touchStartY.current = null;
        }}
        style={{
          background: 'white',
          borderRadius: '24px 24px 0 0',
          width: '100%',
          maxWidth: '480px',
          padding: '12px 24px 36px',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
          position: 'relative',
        }}
      >
        {/* Handle */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D1C9C0', margin: '0 auto 20px' }} />

        {/* A visible way out, including on the mandatory gate. Dismissing does not
            bypass anything: the sheet returns the moment they try to type. What it
            buys them is the ability to go back and READ the conversation, which a
            sheet with no exit made impossible. */}
        {!justRegistered && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              position: 'absolute', top: 10, right: 14,
              width: 32, height: 32, borderRadius: '50%',
              border: 'none', background: '#F4F0EA', color: '#6b615a',
              fontSize: 16, lineHeight: 1, cursor: 'pointer',
              // Sits above the sheet content: without this the block that follows
              // painted over it and ate the tap, so the close looked dead.
              zIndex: 5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        )}

        {/* Welcome — shown INSTEAD of the form once the account exists, so the first thing
            they see after handing over an email is proof it was worth it. */}
        {/* Welcome — tiles. Tapping one opens that feature's example (below) rather than
            navigating straight to an empty section. */}
        {justRegistered && !exampleKey && (
          <div style={{ textAlign: 'center', padding: '4px 0 8px' }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%', background: '#F6EFE4',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
            }}>
              <svg width="22" height="22" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1.5 5l2.5 2.5 4.5-5" stroke="#C9A84C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: '0 0 6px' }}>
              {firstName ? `You're in, ${firstName}` : "You're in"}
            </h2>
            <p style={{ fontSize: 14, color: '#888', lineHeight: 1.5, margin: '0 0 18px' }}>
              Your profile is saved. Here's what it unlocks:
            </p>

            <div style={{ marginBottom: 16 }}>
              <FeatureExampleRows onSelect={setExampleKey} />
            </div>

            <button
              type="button"
              onClick={() => { onClose(); resetModal(); }}
              style={{
                width: '100%', border: 'none', borderRadius: 100, padding: '13px 16px',
                background: '#2A1212', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Keep chatting
            </button>
          </div>
        )}

        {/* Welcome — single example for the tapped tile: the real screen + its own action CTA. */}
        {exampleKey && EXAMPLES[exampleKey] && (
          <div style={{ padding: '2px 0 4px' }}>
            <button
              type="button"
              onClick={() => setExampleKey(null)}
              style={{
                background: 'none', border: 'none', color: '#6b615a', fontSize: 15, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 4px', margin: '0 0 10px',
              }}
            >
              <span aria-hidden="true">‹</span> Back
            </button>
            {/* The example is the whole point of tapping a tile, so give it the
                screen rather than a letterboxed strip. */}
            {/* No fixed-height frame. A portrait screenshot centred in a wide box
                left cream bars either side; now the container IS the image. */}
            <img
              src={EXAMPLES[exampleKey].img}
              alt={EXAMPLES[exampleKey].cap}
              loading="eager"
              style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 16 }}
            />
            <p style={{ margin: '14px 6px 16px', textAlign: 'center', fontSize: 14, lineHeight: 1.45, color: '#5A5049' }}>
              {EXAMPLES[exampleKey].cap}
            </p>
            {/* Before registering the feature isn't theirs to open yet, so the
                example ends in the ask rather than a button that would bounce
                them. After registering it opens the real screen. */}
            <button
              type="button"
              onClick={() => {
                if (justRegistered) { onExplore?.(exampleKey); resetModal(); return; }
                setExampleKey(null);
                setCameFromPitch(true);
                setShowPitch(false);
              }}
              style={{
                width: '100%', border: 'none', borderRadius: 100, padding: '14px 16px',
                background: '#2A1212', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {justRegistered ? EXAMPLES[exampleKey].cta : 'Create a free account'}
            </button>
          </div>
        )}

        {/* Mode tabs */}
        {!justRegistered && showPitch && !exampleKey && (
          <div style={{ padding: '2px 0 4px' }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: '0 0 6px', paddingRight: 40 }}>
              Keep what Son:mi learns about you
            </h2>
            <p style={{ fontSize: 16, color: '#786D66', lineHeight: 1.5, margin: '0 0 16px' }}>
              Create a free account to save your skin profile, conversations, matches and routine in one place.
            </p>
            <p style={{ fontSize: 14, color: '#928981', margin: '0 0 10px' }}>
              Tap any one to see a real example
            </p>

            <div style={{ marginBottom: 16 }}>
              <FeatureExampleRows onSelect={setExampleKey} />
            </div>

            <button
              type="button"
              onClick={() => { setCameFromPitch(true); setShowPitch(false); }}
              style={{
                width: '100%', border: 'none', borderRadius: 100, padding: '13px 16px',
                background: '#2A1212', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Create a free account
            </button>
            <p style={{ textAlign: 'center', fontSize: 13, color: '#8A8079', margin: '10px 0 0' }}>
              Already a member?{' '}
              <button
                type="button"
                onClick={() => { setMode('signin'); setShowPitch(false); }}
                style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, color: '#8A5A2B', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Sign in
              </button>
            </p>
          </div>
        )}

        {/* Leave room for the close button so the tabs never run underneath it. */}
        {!justRegistered && !showPitch && !exampleKey && (
        <div style={{ display: 'flex', background: '#F5F0EB', borderRadius: 100, padding: 4, marginBottom: 20, marginTop: 28 }}>
          {['create', 'signin'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => switchMode(m)}
              style={{
                flex: 1, border: 'none', borderRadius: 100,
                padding: '9px 16px',
                background: mode === m ? 'white' : 'transparent',
                color: '#2A324B',
                fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
                boxShadow: mode === m ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              {m === 'create' ? 'Create account' : 'Sign in'}
            </button>
          ))}
        </div>
        )}

        {!justRegistered && !showPitch && !exampleKey && mode === 'create' && (
          <>
            {/* Title */}
            {/* Second sheet. It deliberately does NOT restate the pitch: by now the
                decision is made, and the only question left is what happens after.
                Son:mi speaks in the first person everywhere else in chat, so
                continuing in her voice keeps this feeling like one conversation
                rather than a form that appeared. */}
            <div style={{ marginBottom: 10 }}>
              {simpleForm ? (
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: 0, lineHeight: 1.25 }}>Create account to see your colours</h2>
              ) : cameFromPitch ? (
                <>
                  <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: '0 0 6px', lineHeight: 1.25 }}>
                    Almost there <span style={{ color: '#C9A84C' }}>✦</span>
                  </h2>
                  <p style={{ fontSize: 14, color: '#8A8079', lineHeight: 1.5, margin: 0 }}>
                    Then I&rsquo;ll pick up right where we left off.
                  </p>
                </>
              ) : (
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: 0, lineHeight: 1.25 }}>
                  Create a free account{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    to keep going <span style={{ color: '#C9A84C' }}>✦</span>
                  </span>
                </h2>
              )}
            </div>

            {skipPitch && !simpleForm && (
              <div style={{ marginBottom: 18 }}>
                <p style={{ fontSize: 12, color: '#A96F54', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 10px' }}>
                  See what your account keeps ready
                </p>
                <FeatureExampleRows onSelect={setExampleKey} />
              </div>
            )}

            {/* Benefits */}
            {!simpleForm && <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {BENEFITS.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#C9A84C', fontWeight: 700, fontSize: 14, lineHeight: 1 }}>✓</span>
                  <span style={{ fontSize: 14, color: '#2A324B', letterSpacing: '-0.01em' }}>{b.text}</span>
                </div>
              ))}
            </div>}

            {!simpleForm && <div style={{ height: 1, background: '#F0EBE5', margin: '0 0 18px' }} />}
          </>
        )}

        {!justRegistered && !showPitch && !exampleKey && mode === 'signin' && (
          <div style={{ marginBottom: 18 }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: '0 0 6px' }}>Welcome back</h2>
            <p style={{ fontSize: 14, color: '#888', margin: 0 }}>Sign in to access your saved profile.</p>
          </div>
        )}

        {/* Forgot password inline form */}
        {!justRegistered && !showPitch && !exampleKey && mode === 'signin' && showReset && (
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => { setShowReset(false); setResetSent(false); setError(''); setResetEmail(''); }}
              style={{ background: 'none', border: 'none', color: '#AFA99F', fontSize: 13, cursor: 'pointer', padding: '0 0 10px', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Back to sign in
            </button>
            {resetSent ? (
              <div style={{ background: '#E8F5E9', borderRadius: 12, padding: '16px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>📬</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Check your inbox</div>
                <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                  We sent a password reset link to <strong>{resetEmail}</strong>. It may take a minute to arrive.
                </div>
              </div>
            ) : (
              <form onSubmit={handleSendReset} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ marginBottom: 2 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#1A1A1A', marginBottom: 4 }}>Reset your password</div>
                  <div style={{ fontSize: 13, color: '#888' }}>Enter your email and we'll send you a reset link.</div>
                </div>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  style={inputStyle}
                  autoFocus
                />
                {error && (
                  <div style={{ color: '#D32F2F', fontSize: 13, padding: '10px 12px', background: '#FFEBEE', borderRadius: 8 }}>
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={resetLoading}
                  style={{
                    width: '100%', padding: '16px', border: 'none',
                    borderRadius: 100,
                    background: resetLoading ? '#CCC' : '#341C1C',
                    color: 'white', fontSize: 16, fontWeight: 600,
                    cursor: resetLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {resetLoading ? 'Sending…' : 'Send reset link'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Form — only rendered when not in reset mode (prevents hidden-form submission) */}
        {!justRegistered && !showPitch && !exampleKey && !(mode === 'signin' && showReset) && <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {mode === 'create' && (
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="First name"
              style={inputStyle}
            />
          )}

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="your@email.com"
            style={inputStyle}
          />

          {/* Password with show/hide */}
          <div style={{ position: 'relative' }}>
            <input
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={mode === 'create' ? 'Create a password' : 'Password'}
              style={{ ...inputStyle, paddingRight: 44 }}
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              style={{
                position: 'absolute', right: 14, top: '50%',
                transform: 'translateY(-50%)',
                background: 'none', border: 'none',
                cursor: 'pointer', color: '#AFA99F',
                display: 'flex', alignItems: 'center', padding: 0,
              }}
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>

          {/* Marketing opt-in (create only) */}
          {mode === 'create' && (
            <div
              onClick={() => setMarketingOptIn((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: marketingOptIn ? 'linear-gradient(135deg, #F0F5FC, #E8F0FA)' : 'linear-gradient(135deg, #FDF8F0, #FDF3E8)',
                border: `1.5px solid ${marketingOptIn ? '#3B6B9E' : '#E8D4BC'}`,
                borderRadius: 14, padding: 14, cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: 6,
                border: `1.5px solid ${marketingOptIn ? '#3B6B9E' : '#D1C9C0'}`,
                background: marketingOptIn ? '#3B6B9E' : 'white',
                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}>
                {marketingOptIn && (
                  <svg width="11" height="11" viewBox="0 0 10 10" fill="none">
                    <path d="M1.5 5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div style={{ flex: 1 }}>
                {/* Scoped to the NEWSLETTER only. Account and product emails are service
                    messages, not marketing, so they must not be implied by this consent —
                    and saying so keeps those sends lawful without a tick. */}
                <div style={{ fontSize: 14, fontWeight: 700, color: '#341C1C' }}>
                  Send me the weekly Son:mi skin letter
                </div>
                <div style={{ fontSize: 13, color: '#7A5C4A', marginTop: 3 }}>
                  {/* One line at mobile width. "Unsubscribe any time" is dropped rather
                      than the scoping — every send carries an unsubscribe link anyway,
                      whereas separating marketing from account email is the point here. */}
                  Separate from account emails.
                </div>
              </div>
            </div>
          )}

          {/* Strength bars + rules (create only) */}
          {mode === 'create' && password.length > 0 && (
            <div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} style={{
                    flex: 1, height: 3, borderRadius: 2,
                    background: i < pwScore ? (STRENGTH_COLORS[pwScore - 1] || '#EDE8E3') : '#EDE8E3',
                    transition: 'background 0.3s',
                  }} />
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {PW_RULES.map((rule, i) => (
                  <div key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: pwMet[i] ? '#4A9B6F' : '#AFA99F' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: pwMet[i] ? '#4A9B6F' : '#D1C9C0', flexShrink: 0, transition: 'background 0.2s' }} />
                    {rule.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div style={{ color: '#D32F2F', fontSize: 13, padding: '10px 12px', background: '#FFEBEE', borderRadius: 8 }}>
              {error}
            </div>
          )}
          {message && (
            <div style={{ color: '#2E7D32', fontSize: 13, padding: '10px 12px', background: '#E8F5E9', borderRadius: 8 }}>
              {message}
            </div>
          )}


          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%', padding: '16px', border: 'none',
              borderRadius: 100,
              background: isLoading ? '#CCC' : '#341C1C',
              color: 'white', fontSize: 16, fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              marginTop: 6,
            }}
          >
            {/* Not "Remember my skin profile" — most people reaching this gate have only
                chatted, and promising to remember a skin read they never did is the same
                overclaim as the old "I've seen your skin now" headline. */}
            {/* "Create free account" describes the paperwork; this describes the point.
                It's the old CTA minus "profile" — asking Son:mi to remember you is true
                whether or not a scan happened, whereas "remember my skin profile" implied
                a skin read that most people at this gate haven't done. The requirement is
                still stated plainly in the line underneath. */}
            {isLoading ? 'Loading…' : mode === 'create' ? 'Remember my skin' : 'Sign in'}
          </button>

          {mode === 'create' && (
            <>
              {mandatory ? (
                <button
                  type="button"
                  onClick={handleClose}
                  style={{ background: 'none', border: 'none', color: '#AFA99F', fontSize: 14, cursor: 'pointer', textAlign: 'center', padding: '4px 0' }}
                >
                  Close and read the chat
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleClose}
                  style={{ background: 'none', border: 'none', color: '#AFA99F', fontSize: 14, cursor: 'pointer', textAlign: 'center', padding: '4px 0' }}
                >
                  Continue without saving
                </button>
              )}
            </>
          )}

          {mode === 'signin' && (
            <button
              type="button"
              onClick={() => { setShowReset(true); setResetEmail(email); setError(''); }}
              style={{ background: 'none', border: 'none', color: '#AFA99F', fontSize: 14, cursor: 'pointer', textAlign: 'center', padding: '2px 0' }}
            >
              Forgot password?
            </button>
          )}

          <p style={{ fontSize: 14, color: '#B0A89E', textAlign: 'center', margin: '8px 0 0', lineHeight: 1.5 }}>
            By using Son:mi, you agree to our{' '}
            <a href="https://sonmi.io/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#B0A89E', textDecoration: 'underline' }}>Terms of Service</a>
            {' '}and{' '}
            <a href="https://sonmi.io/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#B0A89E', textDecoration: 'underline' }}>Privacy Notice</a>.
          </p>
        </form>}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: '#F5F0EB',
  border: 'none',
  borderRadius: 12,
  padding: '14px 16px',
  fontSize: 14,
  color: '#2A324B',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
